import type { BranchKey, WorkspaceKey } from '@festina-life/api'
import type { Optional } from '@festina-life/core'
import { CircularSpinner } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { withSuspense } from '@festina-life/ui/libs'
import { useEffect, useState, useTransition } from 'react'

import { isProtectedBranchCategory } from '../../../../uiModels/branches/types'
import { useConciseBranch } from '../../../../uiModels/branches/useConciseBranch'
import type { ConciseChangesetWithBranchKey } from '../../../../uiModels/changesets/types'
import { useConciseChangesets } from '../../../../uiModels/changesets/useConciseChangesets'
import type { ApprovalStatus } from '../../../configurationItemsTree/types'
import { BranchChangesetListOptions } from './BranchChangesetListOptions'
import { ConciseChangesetDisclosure } from './ConciseChangesetDisclosure'
import type { BranchConciseChangesetListVariant } from './types'
import { useFilteredBranchChangesets } from './useFilteredBranchChangesets'

const MAX_OPERATIONS_IN_SEARCH_TO_OPEN_DISCLOSURE = 50

type Props = {
  branchKey: BranchKey
  workspaceKey: Optional<WorkspaceKey>
  variant: BranchConciseChangesetListVariant
}

const BranchConciseChangesetListInternal = ({
  branchKey,
  workspaceKey,
  variant,
}: Props) => {
  const translate = useTranslate()
  const [, startTransition] = useTransition()
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<
    Record<ApprovalStatus, boolean>
  >({
    approved: variant === 'review' ? false : true,
    pending: true,
  })
  const [defaultDisclosureStatus, setDefaultDisclosureStatus] =
    useState<Optional<'expanded' | 'collapsed'>>(undefined)

  const shouldIncludeBaseConciseChangesets = variant !== 'review'
  const filteredChangesets = useFilteredBranchChangesets({
    branchKey,
    query,
    statusFilter,
    workspaceKey,
    shouldIncludeBaseConciseChangesets,
  })
  const conciseBranch = useConciseBranch({
    branchName: branchKey.name,
    server: branchKey.server,
    shouldFallbackToStandard: true,
  })
  const unfilteredChangesets = useConciseChangesets({
    branchKey,
    workspaceKey,
    shouldIncludeBaseConciseChangesets,
  })

  const getChangesetOrigin = (changeset: ConciseChangesetWithBranchKey) => {
    if (changeset.branchKey.name === conciseBranch?.baseBranchName) {
      return 'baseBranch'
    }

    return changeset.branchKey.name === conciseBranch?.followsBranchName
      ? 'followedBranch'
      : 'branch'
  }

  const handleQueryChanged = (nextQuery: string) => {
    startTransition(() => {
      setQuery(nextQuery)
    })
  }

  const ownChangesets = unfilteredChangesets.filter(
    (changeset) => getChangesetOrigin(changeset) === 'branch',
  )
  const isProtected = isProtectedBranchCategory(conciseBranch?.branchCategory)

  useEffect(() => {
    setDefaultDisclosureStatus(undefined)
  }, [query, statusFilter])

  const ownChangesetKeys = new Set(
    ownChangesets.map((changeset) => changeset.changesetKey),
  )

  return (
    <div className="overflow-x-hidden flex flex-col min-w-0 @container p-1 gap-y-0.5!">
      <BranchChangesetListOptions
        variant={variant}
        query={query}
        onQueryChanged={handleQueryChanged}
        onExpandAll={() => {
          setDefaultDisclosureStatus('expanded')
        }}
        onCollapseAll={() => {
          setDefaultDisclosureStatus('collapsed')
        }}
        statusFilter={statusFilter}
        onStatusFilterChanged={setStatusFilter}
      />
      {filteredChangesets.length ? (
        filteredChangesets.map((changeset) => {
          const shouldDefaultOpen =
            !!changeset.operations?.length &&
            changeset.operations?.length <
              MAX_OPERATIONS_IN_SEARCH_TO_OPEN_DISCLOSURE

          const isOwnChangeset = ownChangesetKeys.has(changeset.changesetKey)
          return (
            <ConciseChangesetDisclosure
              key={`${changeset.changesetKey}-${!!query}-${shouldDefaultOpen}-${JSON.stringify(statusFilter)}-${defaultDisclosureStatus}`}
              changeset={{
                ...changeset,
                // If not changeset key exists for pulling we use an empty string and allow the pull call to fail and be presented to the user.
                changesetKey: changeset.changesetKey ?? '',
              }}
              branchKey={branchKey}
              isForceExpanded={
                defaultDisclosureStatus === 'expanded' ||
                (!!query.length &&
                  shouldDefaultOpen &&
                  defaultDisclosureStatus !== 'collapsed')
              }
              origin={getChangesetOrigin(changeset)}
              isPullable={isOwnChangeset && !isProtected}
            />
          )
        })
      ) : (
        <div className="opacity-50">{translate('empty')}</div>
      )}
    </div>
  )
}

export const BranchConciseChangesetList = withSuspense(
  BranchConciseChangesetListInternal,
  { fallback: <CircularSpinner className="mx-auto my-2 size-8" /> },
)
